import { BodyBox, EntityForm, Icon, NotFoundPage, pxToRem } from '@lbcamden/lib-react';
import { skipToken } from '@tanstack/react-query';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { PageBreadcrumbs } from '~/client/components/shared/PageBreadcrumbs';
import {
  useGetPersonFullName,
  useProperty,
  useReferenceListSelectOptions,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { useGoBackOrNavigate } from '~/client/hooks/useGoBackOrNavigate';
import { api } from '~/client/lib/trpc';
import { getPropertyBreadcrumbs, toPersonPage, toPropertyPage } from '~/client/utils/navigation';
import { personSearchInputSchema } from '~/common/schemas/people';
import { PersonSearchInput, PersonSearchResult } from '~/common/types/people';

import {
  newHouseholdMemberFormSchema,
  formValuesToCreatePayload,
  getNewHouseholdMemberFormConfig,
  NewHouseholdMemberFormValues,
  personSearchFormConfig,
  getInitialFormValues,
} from './config';
import { PersonSearchResults } from './PersonSearchResults';

type CreationStep = 'search' | 'results' | 'create';

const title = 'New household member';

export const HouseholdMemberCreatePage: FunctionComponent = () => {
  const navigate = useGoBackOrNavigate();

  const { propertyId: propertyIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);

  const userStore = useUserStore();
  const refListSelectOptions = useReferenceListSelectOptions();
  const property = useProperty(propertyId);
  const getPersonFullName = useGetPersonFullName();

  const [step, setStep] = useState<CreationStep>('search');
  const [searchInput, setSearchInput] = useState<PersonSearchInput>();
  const [selectedResult, setSelectedResult] = useState<PersonSearchResult>();
  const [shouldEndPrevHouseholdMember, setShouldEndPrevHouseholdMember] = useState<boolean>();

  const {
    data: searchResults,
    isLoading: searching,
    isError: hasSearchError,
  } = api.personSearch.useQuery(searchInput || skipToken);

  const { data: existingPersonValues, isLoading: loadingValues } =
    api.newHouseholdMemberFormValues.useQuery(
      selectedResult ? { propertyId, personId: selectedResult.personId } : skipToken,
    );

  // Exclude people already living at the property
  const filteredSearchResults = useMemo(
    () =>
      hasSearchError ? [] : searchResults?.filter((result) => result.propertyId !== propertyId),
    [searchResults, hasSearchError],
  );

  const newHouseholdMemberFormConfig = useMemo(
    () => getNewHouseholdMemberFormConfig(refListSelectOptions),
    [refListSelectOptions],
  );

  const initialFormValues = useMemo(
    () => getInitialFormValues(existingPersonValues || searchInput),
    [existingPersonValues, searchInput],
  );

  const hasSearchResults = !!filteredSearchResults?.length;

  const goToPropertyPage = () => navigate(toPropertyPage(propertyId));

  const goToPersonPage = (newPersonId: number) => navigate(toPersonPage(propertyId, newPersonId));

  const onSearchStepUpdate = () => {
    if (step !== 'search' || !filteredSearchResults) {
      return;
    }
    setStep(hasSearchResults ? 'results' : 'create');
  };

  const onResultsStepUpdate = () => {
    if (step !== 'results' || (!existingPersonValues && selectedResult)) {
      return;
    }
    setStep('create');
  };

  const onContinueToCreate = (result?: PersonSearchResult, shouldEndPrev?: boolean) => {
    setShouldEndPrevHouseholdMember(shouldEndPrev);

    if (result === selectedResult) {
      onResultsStepUpdate();
    } else {
      setSelectedResult(result);
    }
  };

  const onSave = (values: NewHouseholdMemberFormValues) => {
    const existingPerson = selectedResult && {
      ...selectedResult,
      ...existingPersonValues,
    };

    const payload = formValuesToCreatePayload(
      propertyId,
      values,
      getPersonFullName,
      existingPerson,
      shouldEndPrevHouseholdMember,
    );
    const newMemberPersonId = payload.values.personId;

    userStore?.handleAndDispatchMessage('createHouseholdMember', newMemberPersonId, payload, {
      newEntityTempId: existingPerson && newMemberPersonId,
    });
    void goToPersonPage(newMemberPersonId);
  };

  useEffect(onSearchStepUpdate, [searchInput, filteredSearchResults]);

  useEffect(onResultsStepUpdate, [selectedResult, existingPersonValues]);

  if (!property) {
    return <NotFoundPage />;
  }

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[...getPropertyBreadcrumbs(property), { name: title }]}
      />
      {step === 'search' && (
        <EntityForm<PersonSearchInput>
          title={title}
          schema={personSearchInputSchema}
          config={personSearchFormConfig}
          initialValue={searchInput}
          loading={searching}
          canSubmitWithoutChanges
          saveLabel={
            <>
              Next <Icon icon="ui/arrow-light" width={pxToRem(14)} $flipX />
            </>
          }
          onCancel={goToPropertyPage}
          onSave={setSearchInput}
        />
      )}
      {step === 'results' && hasSearchResults && (
        <PersonSearchResults
          results={filteredSearchResults}
          loading={loadingValues}
          onClickBack={() => setStep('search')}
          onContinue={onContinueToCreate}
        />
      )}
      {step === 'create' && (
        <EntityForm<NewHouseholdMemberFormValues>
          title={title}
          schema={newHouseholdMemberFormSchema}
          config={newHouseholdMemberFormConfig}
          initialValue={initialFormValues}
          canSubmitWithoutChanges={!!existingPersonValues}
          cancelLabel={
            <>
              <Icon icon="ui/arrow-light" width={pxToRem(14)} /> Back
            </>
          }
          onCancel={() => setStep(hasSearchResults ? 'results' : 'search')}
          onSave={onSave}
        />
      )}
    </BodyBox>
  );
};
